export interface Amount {
    amount: number;
    currency: string;
}

export interface AccountingReportFilter {
    reservationDateFrom: string | null;
    reservationDateTo: string | null;
    flightDateFrom: string | null;
    flightDateTo: string | null;
    depPort: string | null;
    arrPort: string | null;
    reservationType: string | null;
    providerCode: string | null;
    airlineCode: string | null;
    page: number | null;
    size: number | null;
    sortBy: string | null;
    sortDirection: string | null;
}

export interface Metrics {
    revenue: Amount;
    cost: Amount;
    profit: Amount;
    profitRate: number;
    marginPercentage: number;
}

export interface IAccountingSummaryUnit {
    categoryName: string;
    metrics: Metrics;
}

export interface Route {
    code: string;
    name: string;
    country: string;
}

export interface FlightBasedDetails {
    segmentType: 'DEPARTURE' | 'RETURN';
    flight: Metrics;
    tax: Amount;
    shoppingCategories: Record<string, Metrics>;
}

export interface ReservationProducts {
    [key: string]: Metrics;
}

export interface IReservationContent {
    reservationType: string;
    flightType: string;
    pnr: string;
    airlinePnr: string;
    agent: string;
    airline: string;
    reservationDate: Date | string;
    flightDate: Date | string;
    from: Route;
    to: Route;
    paymentMethod: string;
    totalTax: Amount;
    ticket: Metrics;
    commission: Amount;
    other: Metrics;
    reservationProducts: ReservationProducts;
    total: Metrics;
    flightBasedDetails: FlightBasedDetails[];
}

export interface IReservationAccountingResponse {
    content: IReservationContent[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: {
        sort: {
            sorted: boolean;
            unsorted: boolean;
            empty: boolean;
        };
        pageNumber: number;
        pageSize: number;
        offset: number;
        paged: boolean;
        unpaged: boolean;
    };
    sort: {
        sorted: boolean;
        unsorted: boolean;
        empty: boolean;
    };
}

export interface TableState {
    page: number;
    size: number;
    sortBy: string | null;
    sortDirection: 'ASC' | 'DESC' | null;
}

export interface IAccountingMonthlyReport {
    monthly: IAccountingMonthlyReportUnit[];
    total: Metrics;
    year: number;
    totalPassengerCount: number;
    totalReservationCount: number;
}

export interface IAccountingMonthlyReportUnit {
    month: number;
    metrics: Metrics;
    passengerCount: number;
    reservationCount: number;
}

export interface TotalSummary {
    ticket: Metrics;
    total: Metrics;
    commission: Amount;
    other: Metrics;
    reservationProducts: ReservationProducts;
    paymentMethod: string;
    tax: Amount;
}

export interface TripCountData {
    oneWayCount: number;
    roundTripCount: number;
}

export interface FilterOptions {
    reservationFrom: Date | null;
    reservationTo: Date | null;
    flightDateFrom: Date | null;
    flightDateTo: Date | null;
    flightFromPort: string | null;
    flightToPort: string | null;
    reservationType: { name: string; value: string } | null;
    provider: string | null;
    airline: string | null;
}

export interface ReservationColumn {
    key: string;
    label: string;
}

export interface TabItem {
    title: number;
    value: number;
}

export interface TableRow {
    key: string;
    label: string;
}

export interface ReservationRowData extends IReservationContent {
    __key: string;
}

export interface Airlines {
    name: string;
    code: string;
}

export interface Providers {
    id: number;
    code: string;
    explanation: string;
}
export interface Ports {
    code: string;
    explanation: string;
    country: string;
    countryName: string;
    city: string;
}

export enum BookingDateOrFlightDate {
    BookingDate = 'BOOKING',
    FlightDate = 'FLIGHT'
}

export interface MonthlyComparisonData {
    month: number;
    comparisonMetrics: Metrics;
    passengerCount: number;
    reservationCount: number;
}

export interface YearlyComparisonData {
    year1: number;
    year2: number;
    year1Report: IAccountingMonthlyReport;
    year2Report: IAccountingMonthlyReport;
    monthlyComparisons: MonthlyComparisonData[];
    yearlyComparison: {
        comparisonMetrics: Metrics;
        passengerCount: number;
        reservationCount: number;
    };
    datetype: string;
}
